// RFI / innsamlingsstyring (intel): opprett informasjonsbehov, se åpne/forfalte,
// tildel (tasking) og besvar. Deles i sanntid over hubben (org-scope). Docked
// GNÅ-panel. Ren DOM. Lukker OODA-loopen: krav → RFI → tasking → svar.
import {
  sortRfis, analystQueue, RfiSchema, answerRfi, nextRfiStatus, formatMgrs,
  RfiPriority, PRECEDENCE_LABEL, PRECEDENCE_CODEWORD, precedenceOrder,
  REQUIREMENT_LABEL, RequirementSchema, RequirementKind, CollectionTaskSchema,
  type Rfi, type RfiPriority as RfiPriorityId, type Requirement, type CollectionTask,
} from '@skade/contracts';
import { KONTEKST_FELT, type Kontekst } from '../kontekst.js';
import { KontekstBlokk } from './kontekstblokk.js';
import { tolkMgrsFelt } from './stedtid.js';
import { sporTekst } from './shell.js';

// Hastegrad: kodeord + rekkefølge kommer fra contracts (`precedence.ts`), ikke
// fra en liste her. Sto de lokalt, kunne dette panelet og logistikk-panelet vise
// ulike ord for samme presedens — som de FAKTISK gjorde («Immediate» her, rå
// «immediate» der). Der ligger også begrunnelsen for at NATO-kodeordene ikke
// oversettes; les den før du endrer teksten.
const PRIO: RfiPriorityId[] = precedenceOrder(RfiPriority.options);
const PRIO_COLOR: Record<RfiPriorityId, string> = {
  flash: '#ff3b3b', immediate: '#ff9a3b', priority: '#ffd24a', routine: '#8fb0c8',
};
const STATUS_LABEL: Record<Rfi['status'], string> = {
  open: 'Åpen', tasked: 'Tildelt', answered: 'Besvart', closed: 'Lukket',
};
const KRAV_KINDS = RequirementKind.options;

/** Én tildelbar ressurs (typisk fra kapabilitetsregisteret) — id + lesbart navn. */
export interface Ressurs { id: string; navn: string }

export interface RfiHooks {
  self: string;
  getRfis: () => Rfi[];
  /** Kringkast en (ny/oppdatert) RFI over hubben + oppdater lokalt. */
  publish: (r: Rfi) => void;
  /** Ferdig kontekst (hvem/hvor/når/enhet) — ÉN kilde, se `kontekst.ts`. */
  kontekst: () => Kontekst;
  /** Tida sendes inn; panelet leser ikke klokka selv. */
  naa: () => number;
  /**
   * Etterretningskravene (PIR/CCIR/FFIR/EEFI) RFI-ene henger under.
   *
   * Trengs for TRIAGEN: `analystQueue` veier en RFI koblet til en PIR tyngre enn
   * en uten kobling, fordi en PIR er sjefens uttalte informasjonsbehov. Uten
   * kravene faller den vektingen bort — den blir ikke gal, bare fattigere.
   */
  getRequirements?: () => Requirement[];
  /** Åpne/lukke observasjoner koblet til en RFI (arkivet svarer). */
  onVisSvar?: (r: Rfi) => void;
  /**
   * KART FØRST (UX_DESIGNSYSTEM.md §2). Arm neste kartklikk; kalles med MGRS-
   * referansen for punktet operatøren klikket (main.ts eier selve
   * `map.on('click', …)` via `kartmodus` — samme arbiter som «plasser
   * stridsmelding»/«plasser kapabilitet»). Udefinert i tester/uten kart —
   * knappen vises da ikke, og feltet under er fortsatt et gyldig fallback.
   */
  armStedKlikk?: (ferdig: (mgrs: string) => void) => void;
  /**
   * SENS-32: opprett et etterretningskrav (PIR/CCIR/FFIR/EEFI) mot hubben
   * (`POST /innhenting/krav`). Udefinert i tester/uten hub — «Nytt krav»-
   * skjemaet vises da ikke, samme fravær-mønster som `armStedKlikk`.
   */
  sendKrav?: (k: Requirement) => Promise<unknown>;
  /** Kalt etter et vellykket `sendKrav` — main.ts bruker denne til å hente
   * `GET /innhenting` på nytt, slik at det nye kravet dukker opp i triagen
   * (`getRequirements`) uten at operatøren må laste siden på nytt. */
  onKravOpprettet?: () => void;
  /**
   * SENS-32: tildel et innsamlingsoppdrag — kobler en ressurs til en åpen RFI
   * (`POST /innhenting/oppdrag`; oppdraget er teknisk knyttet til RFI-en via
   * `rfiId`, ikke direkte til kravet — kravet nås via RFI-ens `requirementId`).
   * Udefinert i tester/uten hub — «Tildel oppdrag»-seksjonen vises da ikke.
   */
  sendOppdrag?: (t: CollectionTask) => Promise<unknown>;
  /**
   * Tildelbare ressurser — typisk kapabilitetsregisteret
   * (`kapabilitetsplanlegger.ts`), gjenbrukt her i stedet for et fritekstfelt.
   * Udefinert/tom liste faller tilbake til fritekst («annet»-feltet er alltid
   * synlig som fallback for ressurser som ikke (ennå) er i registeret).
   */
  getRessurser?: () => Ressurs[];
}

export class RfiPanel {
  readonly el: HTMLElement;
  private open = false;
  private listEl!: HTMLElement;
  private blokk!: KontekstBlokk;
  private kravListEl!: HTMLElement;
  private kravMelding!: HTMLElement;
  private tildelMelding!: HTMLElement;
  private tildelRfiSel: HTMLSelectElement | null = null;
  private tildelRessursSel: HTMLSelectElement | null = null;

  constructor(private hooks: RfiHooks) {
    this.el = this.build();
    this.render();
  }

  toggle() {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    // DTG friskes opp ved hver åpning — et RFI-skjema som viser åpningstida fra
    // i går er verre enn ingen DTG.
    if (this.open) { this.blokk.oppfrisk(); this.render(); }
  }

  private build(): HTMLElement {
    const p = div('report-composer rfi-panel');
    const head = div('report-head');
    head.append(span('report-title', 'RFI / innsamlingsstyring'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);

    // Opprett-skjema. Avsender/enhet/DTG er ferdig utfylt (kontekst-blokka);
    // posisjonsraden er AV — en RFI er et spørsmål, ikke en observasjon på et
    // sted, og et koordinat her ville vært pynt uten mening.
    const form = div('report-form');
    this.blokk = new KontekstBlokk({ hent: () => this.hooks.kontekst(), posisjon: false });
    form.append(this.blokk.el);
    const q = document.createElement('textarea');
    q.className = 'report-input'; q.placeholder = 'Informasjonsbehov (f.eks. «Er brua ved Sanne farbar for stridsvogn?»)';
    // Sted (UX-funn 8): interesseområdet SPØRSMÅLET gjelder (NAI) — noe helt
    // annet enn avsenderens posisjon (som posisjonsraden over er slått AV for).
    // Valgfritt: en RFI uten sted er fortsatt et gyldig spørsmål, men et oppgitt
    // sted valideres — et halvt koordinat på et kart er verre enn ingen.
    const sted = document.createElement('input');
    sted.className = 'report-input';
    sted.placeholder = 'MGRS, f.eks. 32V NM 02345 66890 (valgfritt)';
    const stedFeil = span('report-error', '');
    stedFeil.hidden = true;
    // KART FØRST: klikk-i-kartet er PRIMÆRVEIEN til stedet, MGRS-feltet er
    // fallback for et kjent koordinat (f.eks. lest fra en annen melding).
    let stedKartBtn: HTMLButtonElement | null = null;
    if (this.hooks.armStedKlikk) {
      stedKartBtn = document.createElement('button');
      stedKartBtn.type = 'button'; stedKartBtn.className = 'targetlist-btn'; stedKartBtn.textContent = 'Plasser i kartet';
      stedKartBtn.onclick = () => {
        this.hooks.armStedKlikk!((mgrs) => { sted.value = formatMgrs(mgrs); stedFeil.hidden = true; });
      };
    }
    const prioSel = document.createElement('select');
    prioSel.className = 'checklist-sel';
    for (const pr of PRIO) { const o = document.createElement('option'); o.value = pr; o.textContent = PRECEDENCE_LABEL[pr]; prioSel.append(o); }
    prioSel.value = 'routine';
    const submit = document.createElement('button');
    submit.className = 'report-submit'; submit.textContent = 'Opprett RFI';
    submit.onclick = () => {
      const question = q.value.trim();
      if (!question) return;
      const stedSvar = tolkMgrsFelt(sted.value);
      if (stedSvar === null) {
        stedFeil.hidden = false;
        stedFeil.textContent = 'Stedet er ikke en gyldig MGRS-referanse — rett det eller la feltet stå tomt.';
        sted.focus();
        return;
      }
      stedFeil.hidden = true;
      const naa = this.hooks.naa();
      const r = RfiSchema.parse({
        id: `rfi-${naa.toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
        // Avsender kommer fra kontekst-blokka (kallesignal / enhet), ikke fra en
        // rå Matrix-id lista måtte vise som «@demo-lagfører:skade». Identiteten
        // stemples uansett autoritativt server-side (grunnloven §4).
        requester: this.blokk.verdi(KONTEKST_FELT.avsender) || this.hooks.self,
        question, priority: prioSel.value as RfiPriorityId,
        status: 'open', createdMs: naa,
        // Både MGRS (det operatøren skrev/leser) og grader (det kartet plotter).
        ...(stedSvar === 'tom' ? {} : { mgrs: stedSvar.mgrs, lat: stedSvar.lat, lon: stedSvar.lon }),
      });
      this.hooks.publish(r);
      q.value = '';
      sted.value = '';
      this.blokk.oppfrisk();
      this.render();
    };
    const stedWrap = labeled('Sted / interesseområde (NAI)', sted);
    if (stedKartBtn) stedWrap.append(stedKartBtn);
    stedWrap.append(stedFeil);
    form.append(labeled('Behov', q), stedWrap, labeled('Hastegrad', prioSel), submit);
    p.append(form);

    this.listEl = div('targetlist-list');
    p.append(this.listEl);

    // ── Nytt etterretningskrav (PIR/CCIR/FFIR/EEFI) — SENS-32 ────────────────
    // Skrivesiden av innhentingsplanen fantes i hubben (`POST /innhenting/krav`)
    // uten UI-flate: en operatør kunne se krav (triagen vekter mot dem, se
    // `getRequirements` over) men ikke OPPRETTE et. Bygges VIDERE PÅ dette
    // panelet (samme dokument-i-OODA-loopen: krav → RFI → tasking → svar), ikke
    // som et nytt frittstående panel — kravet er nabotema, ikke et annet tema.
    // Udefinert `sendKrav` (tester/uten hub) → seksjonen vises ikke, samme
    // fravær-mønster som «Plasser i kartet».
    if (this.hooks.sendKrav) {
      p.append(span('checklist-item-title', 'Nytt etterretningskrav'));
      const kravForm = div('report-form');
      const kindSel = document.createElement('select');
      kindSel.className = 'checklist-sel';
      for (const k of KRAV_KINDS) { const o = document.createElement('option'); o.value = k; o.textContent = REQUIREMENT_LABEL[k]; kindSel.append(o); }
      const kravText = document.createElement('textarea');
      kravText.className = 'report-input';
      kravText.placeholder = 'Hva skal besvares (WHAT), f.eks. «Er fienden i stand til å forsere Glomma ved Sarpsborg?»';
      const kravPrio = document.createElement('input');
      kravPrio.type = 'number'; kravPrio.min = '1'; kravPrio.max = '99'; kravPrio.value = '1'; kravPrio.className = 'report-input';
      const kravNai = document.createElement('input');
      kravNai.className = 'report-input';
      kravNai.placeholder = 'Teig/NAI-referanse (valgfritt)';
      const kravSubmit = document.createElement('button');
      kravSubmit.type = 'button'; kravSubmit.className = 'report-submit'; kravSubmit.textContent = 'Opprett krav';
      kravSubmit.onclick = () => void this.opprettKrav(kindSel.value as Requirement['kind'], kravText, kravPrio, kravNai);
      kravForm.append(
        labeled('Type', kindSel), labeled('Kravtekst', kravText),
        labeled('Prioritet (1 = høyest)', kravPrio), labeled('Teig/NAI (valgfritt)', kravNai),
        kravSubmit,
      );
      p.append(kravForm);
      this.kravMelding = div('checklist-note');
      p.append(this.kravMelding);
      p.append(span('checklist-item-title', 'Eksisterende krav'));
      this.kravListEl = div('targetlist-list');
      p.append(this.kravListEl);
    }

    // ── Tildel oppdrag (tasking) — SENS-32 ───────────────────────────────────
    // En ressurs (fra kapabilitetsregisteret, ELLER fritekst som fallback for
    // ressurser som ikke er registrert der) kobles til en åpen/tildelt RFI.
    // Oppdraget er teknisk knyttet til RFI-en (`rfiId`), som selv kan være
    // koblet til et krav (`Rfi.requirementId`) — se `sendOppdrag`-dokumentasjonen
    // i hooks-typen for hvorfor. Udefinert `sendOppdrag` → seksjonen vises ikke.
    if (this.hooks.sendOppdrag) {
      p.append(span('checklist-item-title', 'Tildel oppdrag'));
      const tildelForm = div('report-form');
      const rfiSel = document.createElement('select');
      rfiSel.className = 'checklist-sel';
      const ressursSel = document.createElement('select');
      ressursSel.className = 'checklist-sel';
      const annetInput = document.createElement('input');
      annetInput.className = 'report-input';
      annetInput.placeholder = 'Ressurs, fritekst (f.eks. kallesignal) — brukes om «annet» er valgt';
      const tildelSubmit = document.createElement('button');
      tildelSubmit.type = 'button'; tildelSubmit.className = 'report-submit'; tildelSubmit.textContent = 'Tildel oppdrag';
      tildelSubmit.onclick = () => void this.tildelOppdrag(rfiSel, ressursSel, annetInput);
      tildelForm.append(
        labeled('RFI', rfiSel), labeled('Ressurs', ressursSel), labeled('Annen ressurs', annetInput), tildelSubmit,
      );
      p.append(tildelForm);
      this.tildelMelding = div('checklist-note');
      p.append(this.tildelMelding);
      // Begge rullelistene fylles i `render()`/`renderTildelValg()`, IKKE her:
      // `hooks.getRessurser` kan lese en variabel (f.eks. kapabilitetsregisteret
      // i main.ts) som ikke er tildelt ennå på dette tidspunktet — konstruktøren
      // her kjører synkront FØR resten av `bootApp()` er ferdig (samme TDZ-fare
      // som `innhentingKrav`-kommentaren i main.ts advarer om).
      this.tildelRfiSel = rfiSel;
      this.tildelRessursSel = ressursSel;
    }

    return p;
  }

  render() {
    const alle = this.hooks.getRfis();
    const now = this.hooks.naa();
    const krav = this.hooks.getRequirements?.() ?? [];
    this.renderKrav(krav);
    this.renderTildelValg(alle);

    // ── TRIAGE, IKKE BARE SORTERING ─────────────────────────────────────────
    //
    // `analystQueue()` har ligget ferdig skrevet og testet med NULL kallsteder
    // (verifisert 2026-08-17). Den veier fire ting med sporbar begrunnelse:
    // hastegrad, FRISTENS nærhet, koblet etterretningskrav (PIR > CCIR > FFIR/EEFI)
    // og ALDER — det siste for at gamle lav-prioritets-RFI-er ikke skal sulte i hjel.
    //
    // ⚠ HVORFOR DETTE ER BEDRE ENN `sortRfis`: en ROUTINE-RFI som forfaller om ti
    // minutter er mer presserende enn en PRIORITY med frist neste uke. Ren
    // hastegrad-sortering setter dem i motsatt rekkefølge, og analytikeren måtte
    // selv holde fristene i hodet. Poengsummen VISES med begrunnelsen, så
    // rangeringen kan etterprøves — den skal aldri være en svart boks (§1).
    //
    // Åpne/tildelte RFI-er triageres. Besvarte/lukkede har ingen kø-verdi, men
    // skal fortsatt være synlige, så de legges under i vanlig rekkefølge.
    const kø = analystQueue(alle, now, krav);
    const iKø = new Set(kø.map((k) => k.rfi.id));
    const resten = sortRfis(alle.filter((r) => !iKø.has(r.id)));
    const poengFor = new Map(kø.map((k) => [k.rfi.id, k]));
    const items = [...kø.map((k) => k.rfi), ...resten];

    this.listEl.innerHTML = '';
    if (!items.length) { this.listEl.append(span('checklist-note', 'Ingen RFI-er ennå.')); return; }
    for (const r of items) {
      const row = div('targetlist-row');
      const dot = document.createElement('span');
      dot.className = 'checklist-dot'; dot.style.background = PRIO_COLOR[r.priority];
      dot.title = `Hastegrad: ${PRECEDENCE_LABEL[r.priority]}`;
      const main = div('checklist-main');
      const tittel = div('checklist-item-title');
      const scored = poengFor.get(r.id);
      if (scored) {
        // Poengsummen først: det er den som bestemmer rekkefølgen, og da skal den
        // være lesbar. Gull = «se hit», systemets ene aksent (UX §9b).
        const p = span('rfi-poeng', String(scored.score));
        p.title = scored.reasons.length
          ? `Triage ${scored.score}/100 — ${scored.reasons.join(', ')}`
          : `Triage ${scored.score}/100`;
        tittel.append(p);
      }
      tittel.append(span('checklist-item-title-tekst', r.question));
      main.append(tittel);
      const overdue = r.dueMs !== undefined && r.dueMs < now && (r.status === 'open' || r.status === 'tasked');
      // Hastegraden skrives ut som kodeord ved siden av fargeprikken: farge alene
      // er ikke lesbar (NVG-modus, fargesyn), og logistikk-panelet skriver den
      // samme veien — samme opplysning, samme form på begge flater.
      // Stedet vises der det finnes (funn 8) — formatert slik det leses opp.
      const sub = span('checklist-note', `${r.requester} · ${PRECEDENCE_CODEWORD[r.priority]} · ${STATUS_LABEL[r.status]}${r.mgrs ? ` · ${formatMgrs(r.mgrs)}` : ''}${overdue ? ' · FORFALT' : ''}${r.answer ? ` · svar: ${r.answer}` : ''}`);
      if (overdue) sub.style.color = '#ff8a8a';
      main.append(sub);
      // Begrunnelsen for poengsummen, i tekst. En rangering operatøren ikke kan
      // etterprøve er en rangering hun ikke bør stole på.
      if (scored?.reasons.length) {
        const kravTekst = r.requirementId
          ? krav.find((k) => k.id === r.requirementId)
          : undefined;
        const deler = [...scored.reasons];
        if (kravTekst) deler.push(`${REQUIREMENT_LABEL[kravTekst.kind]}: ${kravTekst.text.slice(0, 60)}`);
        main.append(span('rfi-begrunnelse', `Triage: ${deler.join(' · ')}`));
      }
      const actions = div('targetlist-actions');
      // Observasjoner i arkivet som er koblet til denne RFI-en — dette er hva som
      // gjør innhentingsplanen AKTIV framfor en liste med spørsmål.
      if (this.hooks.onVisSvar) actions.append(btn('Observasjoner', () => this.hooks.onVisSvar?.(r)));
      if (r.status === 'open') actions.append(btn('Tildel', () => this.advance(r)));
      if (r.status === 'open' || r.status === 'tasked') actions.append(btn('Svar', () => this.answer(r)));
      if (r.status === 'answered') actions.append(btn('Lukk', () => this.advance(r)));
      row.append(dot, main, actions);
      this.listEl.append(row);
    }
  }

  private advance(r: Rfi) {
    const n = nextRfiStatus(r.status);
    if (n) this.hooks.publish({ ...r, status: n });
    this.render();
  }
  private answer(r: Rfi) {
    sporTekst({
      tittel: 'Svar på RFI',
      tekst: r.question,
      flerlinje: true,
      onSvar: (a) => {
        if (a && a.trim()) { this.hooks.publish(answerRfi(r, a.trim(), this.hooks.self, this.hooks.naa())); this.render(); }
      },
    });
  }

  /** Tegn lista over eksisterende etterretningskrav (kun når `sendKrav` er
   * satt — samme fravær-mønster som resten av panelet, se `hooks.sendKrav`). */
  private renderKrav(krav: Requirement[]) {
    if (!this.kravListEl) return;
    this.kravListEl.innerHTML = '';
    if (!krav.length) { this.kravListEl.append(span('checklist-note', 'Ingen etterretningskrav ennå.')); return; }
    for (const k of [...krav].sort((a, b) => a.priority - b.priority || b.createdMs - a.createdMs)) {
      const row = div('targetlist-row');
      const main = div('checklist-main');
      main.append(span('checklist-item-title', `${REQUIREMENT_LABEL[k.kind]} (prio ${k.priority})`));
      main.append(span('checklist-note', k.text + (k.naiRef ? ` · ${k.naiRef}` : '')));
      row.append(main);
      this.kravListEl.append(row);
    }
  }

  /** Fyll RFI-velgeren for tildeling på nytt (kun når `sendOppdrag` er satt). */
  private renderTildelValg(alle: Rfi[]) {
    if (!this.tildelRfiSel) return;
    const forrigeValg = this.tildelRfiSel.value;
    this.tildelRfiSel.replaceChildren();
    const tildelbare = alle.filter((r) => r.status === 'open' || r.status === 'tasked');
    if (!tildelbare.length) {
      const o = document.createElement('option'); o.value = ''; o.textContent = 'Ingen åpne RFI-er å tildele';
      this.tildelRfiSel.append(o);
      this.tildelRfiSel.disabled = true;
      return;
    }
    this.tildelRfiSel.disabled = false;
    for (const r of tildelbare) {
      const o = document.createElement('option');
      o.value = r.id;
      o.textContent = `${r.question.slice(0, 60)} (${STATUS_LABEL[r.status]})`;
      this.tildelRfiSel.append(o);
    }
    if (tildelbare.some((r) => r.id === forrigeValg)) this.tildelRfiSel.value = forrigeValg;

    if (this.tildelRessursSel) {
      const forrigeRessurs = this.tildelRessursSel.value;
      this.tildelRessursSel.replaceChildren();
      const oAnnet = document.createElement('option');
      oAnnet.value = ''; oAnnet.textContent = 'Annet (skriv i feltet under)';
      this.tildelRessursSel.append(oAnnet);
      const ressurser = this.hooks.getRessurser?.() ?? [];
      for (const res of ressurser) {
        const o = document.createElement('option'); o.value = res.id; o.textContent = res.navn; this.tildelRessursSel.append(o);
      }
      if (ressurser.some((res) => res.id === forrigeRessurs)) this.tildelRessursSel.value = forrigeRessurs;
    }
  }

  private async opprettKrav(
    kind: Requirement['kind'], tekst: HTMLTextAreaElement, prio: HTMLInputElement, nai: HTMLInputElement,
  ): Promise<void> {
    const text = tekst.value.trim();
    if (!text) { this.melding(this.kravMelding, 'Skriv hva kravet skal besvare først.', 'varsel'); return; }
    const naa = this.hooks.naa();
    const priority = Math.min(99, Math.max(1, Number(prio.value) || 1));
    const k = RequirementSchema.parse({
      id: `krav-${naa.toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
      kind, text, priority, createdMs: naa,
      ...(nai.value.trim() ? { naiRef: nai.value.trim() } : {}),
    });
    this.melding(this.kravMelding, 'Lagrer …', 'info');
    try {
      await this.hooks.sendKrav!(k);
      this.melding(this.kravMelding, 'Krav opprettet.', 'info');
      tekst.value = ''; nai.value = ''; prio.value = '1';
      this.hooks.onKravOpprettet?.();
      this.render();
    } catch (err) {
      this.melding(this.kravMelding, `⚠ Kunne ikke opprette kravet: ${feilTekst(err)}`, 'feil');
    }
  }

  private async tildelOppdrag(
    rfiSel: HTMLSelectElement, ressursSel: HTMLSelectElement, annet: HTMLInputElement,
  ): Promise<void> {
    const rfiId = rfiSel.value;
    if (!rfiId) { this.melding(this.tildelMelding, 'Ingen åpen RFI å tildele oppdrag til.', 'varsel'); return; }
    const ressursId = ressursSel.value;
    const ressurser = this.hooks.getRessurser?.() ?? [];
    const valgtRessurs = ressursId ? ressurser.find((r) => r.id === ressursId) : undefined;
    const asset = valgtRessurs?.navn || annet.value.trim();
    if (!asset) { this.melding(this.tildelMelding, 'Velg en ressurs, eller skriv en i fritekst-feltet.', 'varsel'); return; }
    const naa = this.hooks.naa();
    const t = CollectionTaskSchema.parse({
      id: `oppdrag-${naa.toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
      rfiId, asset,
      ...(valgtRessurs ? { kapabilitetId: valgtRessurs.id } : {}),
      taskedBy: this.blokk.verdi(KONTEKST_FELT.avsender) || this.hooks.self,
      createdMs: naa,
    });
    this.melding(this.tildelMelding, 'Lagrer …', 'info');
    try {
      await this.hooks.sendOppdrag!(t);
      // Oppdraget er nå tildelt — RFI-en flyttes til «tasked» om den ennå var
      // «open», samme statusflyt som per-rad-knappen «Tildel» alltid har gitt.
      const rfi = this.hooks.getRfis().find((r) => r.id === rfiId);
      if (rfi && rfi.status === 'open') this.hooks.publish({ ...rfi, status: 'tasked' });
      this.melding(this.tildelMelding, `Oppdrag tildelt: ${asset}.`, 'info');
      annet.value = '';
      this.render();
    } catch (err) {
      this.melding(this.tildelMelding, `⚠ Kunne ikke tildele oppdraget: ${feilTekst(err)}`, 'feil');
    }
  }

  private melding(el: HTMLElement, tekst: string, art: 'info' | 'varsel' | 'feil') {
    el.textContent = tekst;
    el.style.color = art === 'feil' ? 'var(--danger)' : art === 'varsel' ? 'var(--gold)' : '';
  }
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function btn(text: string, run: () => void): HTMLButtonElement {
  const b = document.createElement('button'); b.className = 'targetlist-btn'; b.textContent = text; b.onclick = run; return b;
}
function feilTekst(err: unknown): string { return err instanceof Error ? err.message : String(err); }
function labeled(label: string, control: HTMLElement): HTMLElement {
  const wrap = div('report-field'); wrap.append(span('report-field-label', label), control); return wrap;
}
